import React from 'react';

import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import type { FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { numberInputValue } from '@vlabs/monokit/shared/lib/utils';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';

import { useStreamSettingsTranslation } from './i18n';
import type { StreamSettingsSyncResolver } from './types';
import { SynchronizeStatus } from '../sync/SynchronizeStatus';

interface Option<TValue extends string | number> {
  value: TValue;
  label: string;
}

interface TimestampFieldsProps {
  /** Префикс пути полей в форме: 'data' → data.timestamp_source, 'stream_settings.data' → stream_settings.data.timestamp_source. */
  namePrefix: string;
  /** Статусы синхронизации полей с группой; undefined — иконка не показывается. */
  getSyncStatus?: StreamSettingsSyncResolver;
}

// Поля «Данные потока»: формат отсчёта времени, начальное время, угол поворота камеры.
// Общие для настроек группы и источника.
function TimestampFields({
  namePrefix,
  getSyncStatus = undefined,
}: TimestampFieldsProps): React.JSX.Element {
  const { t } = useStreamSettingsTranslation();
  const { control, register, watch } = useFormContext<FieldValues>();

  const timestampOptions: Option<string>[] = [
    { value: 'auto', label: t('timestamp_options.auto') },
    { value: 'pts', label: t('timestamp_options.pts') },
    { value: 'dts', label: t('timestamp_options.dts') },
    { value: 'server', label: t('timestamp_options.server') },
    { value: 'frame_rate', label: t('timestamp_options.frame_rate') },
  ];

  const rotationOptions: Option<number>[] = [0, 90, 180, 270].map((value) => ({
    value,
    label: String(value),
  }));

  const timestampSource = watch(`${namePrefix}.timestamp_source`);

  const syncIcon = (path: string): React.JSX.Element | undefined => {
    const synchronized = getSyncStatus?.(path);
    if (synchronized === undefined) return undefined;

    return <SynchronizeStatus synchronized={synchronized} />;
  };

  return (
    <React.Fragment>
      <FormFieldGroup.Item
        label={(
          <React.Fragment>
            {t('fields.timestamp_source')}
            {syncIcon(`${namePrefix}.timestamp_source`)}
          </React.Fragment>
        )}
        tooltip={t('tooltips.timestamp_source')}
      >
        <Controller
          control={control}
          name={`${namePrefix}.timestamp_source`}
          render={({ field: { value, onChange } }) => (
            <Select
              isClearable
              menuPortalTarget={document.body}
              onChange={(option) => onChange(option?.value ?? undefined)}
              options={timestampOptions}
              placeholder={t('timestamp_options.auto')}
              value={timestampOptions.find((option) => option.value === value)}
            />
          )}
        />
      </FormFieldGroup.Item>

      <FormFieldGroup.Item
        label={(
          <React.Fragment>
            {t('fields.start_time')}
            {syncIcon(`${namePrefix}.pts.start_time`)}
          </React.Fragment>
        )}
        tooltip={t('tooltips.start_time')}
      >
        <Control.Input
          // Поле активно только при timestamp_source = 'pts'
          disabled={timestampSource !== 'pts'}
          type="number"
          {...register(`${namePrefix}.pts.start_time`, { setValueAs: numberInputValue })}
        />
      </FormFieldGroup.Item>

      <FormFieldGroup.Item
        label={(
          <React.Fragment>
            {t('fields.rotation')}
            {syncIcon(`${namePrefix}.rotation`)}
          </React.Fragment>
        )}
        tooltip={t('tooltips.rotation')}
      >
        <Controller
          control={control}
          name={`${namePrefix}.rotation`}
          render={({ field: { value, onChange } }) => (
            <Select
              isClearable
              menuPortalTarget={document.body}
              onChange={(option) => onChange(option?.value ?? undefined)}
              options={rotationOptions}
              placeholder="90"
              value={rotationOptions.find((option) => option.value === value)}
            />
          )}
        />
      </FormFieldGroup.Item>
    </React.Fragment>
  );
}

export { TimestampFields };
